import { ArrowUp, CheckCircle2, Send, ShieldCheck, X } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { UserAvatar } from '@/components/user-avatar';
import { cn } from '@/lib/utils';

import type { QAQuestion } from '../realtime/frames';
import { QA_MAX_RUNES } from '../realtime/frames';

/**
 * The «Вопросы» tab of the webinar side panel (ADR 0010): viewers ask (1..500 runes) and
 * upvote - one per identity, the server dedups - while moderators/admins answer inline or
 * dismiss. The board state arrives entirely from qa/qa_history frames; the own upvotes are
 * tracked locally for the highlighted button state (the wire carries only the count).
 */
export interface QAPanelProps {
  readonly questions: readonly QAQuestion[];
  /** Own identity: marks the question author «вы» and highlights the own questions. */
  readonly ownIdentity: string | null;
  /** True for moderator/admin: the inline answer and dismiss affordances render. */
  readonly canModerate: boolean;
  /** True while the realtime channel is not live: the ask composer disables. */
  readonly disabled: boolean;
  /** Transient server rejection notice (rate_limited / storage_error); clears itself. */
  readonly notice: 'rate_limited' | 'storage_error' | null;
  /** Ids the own identity has upvoted (local tracking; the wire carries only the count). */
  readonly ownUpvotes: ReadonlySet<number>;
  /** Sends the ask; returns false when the text violates the 500-rune contract. */
  readonly onAsk: (text: string) => boolean;
  readonly onUpvote: (questionId: number) => void;
  readonly onAnswer: (questionId: number, text: string) => void;
  readonly onDismiss: (questionId: number) => void;
}

/** Unanswered questions first (oldest last so the newest lands near the composer), then answered. */
function sortQuestions(questions: readonly QAQuestion[]): readonly QAQuestion[] {
  const open: QAQuestion[] = [];
  const answered: QAQuestion[] = [];
  for (const question of questions) {
    (question.answered ? answered : open).push(question);
  }
  return [...open, ...answered];
}

export function QAPanel(props: QAPanelProps): JSX.Element {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [tooLong, setTooLong] = useState(false);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const text = draft.trim();
    if (text.length === 0 || text.length > QA_MAX_RUNES || props.disabled) {
      setTooLong(text.length > QA_MAX_RUNES);
      return;
    }
    if (props.onAsk(text)) {
      setDraft('');
      setTooLong(false);
    } else {
      setTooLong(true);
    }
  }

  const nearCap = draft.trim().length >= QA_MAX_RUNES * 0.9;

  return (
    <div data-testid="qa-panel" className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto p-3 scroll-thin-dark">
        {props.notice !== null ? (
          <p
            role="alert"
            className="rounded-lg bg-red-500/10 px-2.5 py-1.5 text-[11px] text-red-300"
          >
            {props.notice === 'rate_limited'
              ? t('webinar.qa_rate_limited')
              : t('webinar.qa_storage_error')}
          </p>
        ) : null}
        {props.questions.length === 0 ? (
          <p className="px-2 py-6 text-center text-[11.5px] text-zinc-500">
            {t('webinar.qa_empty')}
          </p>
        ) : (
          sortQuestions(props.questions).map((question) => (
            <QuestionRow
              key={question.id}
              question={question}
              own={props.ownIdentity !== null && question.identity === props.ownIdentity}
              upvoted={props.ownUpvotes.has(question.id)}
              canModerate={props.canModerate}
              onUpvote={props.onUpvote}
              onAnswer={props.onAnswer}
              onDismiss={props.onDismiss}
            />
          ))
        )}
      </div>
      <form onSubmit={submit} className="shrink-0 border-t border-white/[0.06] p-3">
        <div className="flex items-center gap-2">
          <input
            data-testid="qa-ask-input"
            aria-label={t('webinar.qa_ask_aria')}
            placeholder={t('webinar.qa_ask_placeholder')}
            value={draft}
            maxLength={QA_MAX_RUNES + 1}
            disabled={props.disabled}
            onChange={(event) => {
              setDraft(event.target.value);
              setTooLong(false);
            }}
            className="h-9 w-full rounded-lg border border-white/10 bg-white/[0.05] px-3 text-[12.5px] text-white outline-none placeholder:text-zinc-600 focus:border-white/20 disabled:opacity-50"
          />
          <button
            type="submit"
            data-testid="qa-ask-send"
            aria-label={t('webinar.qa_send_aria')}
            disabled={props.disabled || draft.trim().length === 0}
            className="grid size-9 shrink-0 place-items-center rounded-lg bg-white text-zinc-950 transition-colors hover:bg-zinc-200 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Send className="size-3.5" />
          </button>
        </div>
        {tooLong ? (
          <p role="alert" className="mt-1 text-[10.5px] text-red-300">
            {t('webinar.qa_too_long', { max: QA_MAX_RUNES })}
          </p>
        ) : nearCap ? (
          <p className="mt-1 text-right text-[10.5px] tabular-nums text-amber-400">
            {QA_MAX_RUNES - draft.trim().length}
          </p>
        ) : null}
      </form>
    </div>
  );
}

interface QuestionRowProps {
  readonly question: QAQuestion;
  readonly own: boolean;
  readonly upvoted: boolean;
  readonly canModerate: boolean;
  readonly onUpvote: (questionId: number) => void;
  readonly onAnswer: (questionId: number, text: string) => void;
  readonly onDismiss: (questionId: number) => void;
}

/** One question card: upvote column, text, author and the moderator answer/dismiss affordances. */
function QuestionRow(props: QuestionRowProps): JSX.Element {
  const { t } = useTranslation();
  const { question } = props;
  const [answering, setAnswering] = useState(false);
  const [answerDraft, setAnswerDraft] = useState('');
  const author = question.displayName.length > 0 ? question.displayName : question.identity;

  function sendAnswer(): void {
    const text = answerDraft.trim();
    props.onAnswer(question.id, text);
    setAnswering(false);
    setAnswerDraft('');
  }

  return (
    <div
      data-testid={`qa-question-${question.id}`}
      className={cn(
        'rounded-xl border p-3 transition-colors',
        question.answered
          ? 'border-white/[0.05] bg-white/[0.02] opacity-60'
          : 'border-white/[0.08] bg-white/[0.03]',
      )}
    >
      <div className="flex items-start gap-2.5">
        <button
          type="button"
          data-testid={`qa-upvote-${question.id}`}
          aria-label={t('webinar.qa_upvote_aria')}
          aria-pressed={props.upvoted}
          disabled={question.answered || props.upvoted}
          onClick={() => {
            if (!props.upvoted) {
              props.onUpvote(question.id);
            }
          }}
          className={cn(
            'flex h-8 w-8 shrink-0 flex-col items-center justify-center rounded-lg border text-[10px] font-bold leading-none transition-colors',
            props.upvoted
              ? 'border-emerald-400/50 bg-emerald-400/15 text-emerald-300'
              : question.answered
                ? 'border-white/[0.06] text-zinc-600'
                : 'border-white/10 bg-white/[0.06] text-zinc-200 hover:border-emerald-400/40 hover:text-emerald-400',
          )}
        >
          <ArrowUp className="size-3" />
          <span className="mt-0.5 tabular-nums">{question.upvotes}</span>
        </button>
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'text-[12.5px] leading-relaxed',
              question.answered ? 'text-zinc-500 line-through' : 'text-zinc-200',
            )}
          >
            {question.text}
          </p>
          <div className="mt-1.5 flex items-center gap-2 text-[10.5px] text-zinc-600">
            <UserAvatar name={author} size="sm" className="size-4 text-[8px]" />
            <span className="truncate">
              {author}
              {props.own ? ` · ${t('webinar.qa_you')}` : ''}
            </span>
            {question.answered ? (
              <span className="inline-flex items-center gap-1 text-emerald-500">
                <CheckCircle2 className="size-3" /> {t('webinar.qa_answered')}
              </span>
            ) : null}
          </div>
          {question.answerText !== undefined && question.answerText.length > 0 ? (
            <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-emerald-400/20 bg-emerald-400/[0.06] p-2 text-[11.5px] leading-relaxed text-emerald-100">
              <ShieldCheck className="mt-0.5 size-3 shrink-0 text-emerald-400" />
              <span className="min-w-0">{question.answerText}</span>
            </div>
          ) : null}
          {props.canModerate && !question.answered ? (
            answering ? (
              <div className="mt-2 flex items-center gap-1.5">
                <input
                  data-testid={`qa-answer-input-${question.id}`}
                  aria-label={t('webinar.qa_answer_aria')}
                  placeholder={t('webinar.qa_answer_placeholder')}
                  value={answerDraft}
                  maxLength={QA_MAX_RUNES + 1}
                  onChange={(event) => {
                    setAnswerDraft(event.target.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      sendAnswer();
                    }
                  }}
                  className="h-7 w-full rounded-md border border-white/10 bg-white/[0.05] px-2 text-[11.5px] text-white outline-none placeholder:text-zinc-600 focus:border-white/20"
                />
                <button
                  type="button"
                  data-testid={`qa-answer-confirm-${question.id}`}
                  aria-label={t('webinar.qa_answer_confirm_aria')}
                  onClick={sendAnswer}
                  className="grid size-7 shrink-0 place-items-center rounded-md bg-white text-zinc-950 transition-colors hover:bg-zinc-200"
                >
                  <Send className="size-3" />
                </button>
                <button
                  type="button"
                  aria-label={t('common.cancel')}
                  onClick={() => {
                    setAnswering(false);
                    setAnswerDraft('');
                  }}
                  className="grid size-7 shrink-0 place-items-center rounded-md border border-white/10 text-zinc-400 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <X className="size-3" />
                </button>
              </div>
            ) : (
              <div className="mt-2 flex items-center gap-1.5">
                <button
                  type="button"
                  data-testid={`qa-answer-${question.id}`}
                  onClick={() => {
                    setAnswering(true);
                  }}
                  className="h-7 rounded-md border border-white/10 px-2 text-[11px] text-zinc-300 transition-colors hover:bg-white/10"
                >
                  {t('webinar.qa_answer')}
                </button>
                <button
                  type="button"
                  data-testid={`qa-dismiss-${question.id}`}
                  onClick={() => {
                    props.onDismiss(question.id);
                  }}
                  className="h-7 rounded-md border border-white/10 px-2 text-[11px] text-zinc-500 transition-colors hover:border-red-400/30 hover:text-red-300"
                >
                  {t('webinar.qa_dismiss')}
                </button>
              </div>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}
